/**
 * SDL Home Screen
 * Main menu with profile, play button, and game modes
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import type { PlayerData as ServerPlayerData } from '@sdl/shared';

export interface PlayerData {
  username: string;
  level: number;
  division: string;
  avatar: string;
  stacks: number;
  gold: number;
  notifications: number;
  hasDailyBonus: boolean;
  bonusStreak: number;
  bonusMultiplier: number;
  nextBonusAt: number | null;
}

/** Shown in the home avatar circle when the player has no custom picture
 *  (fresh account, or after a moderator removes their picture). */
export const DEFAULT_AVATAR = '🎲';

const DEFAULT_PLAYER: PlayerData = {
  username: 'DICEKING99',
  level: 42,
  division: 'D1',
  avatar: DEFAULT_AVATAR,
  stacks: 1200000,
  gold: 850,
  notifications: 0,
  hasDailyBonus: true,
  bonusStreak: 0,
  bonusMultiplier: 1,
  nextBonusAt: null,
};

export class HomeScreen extends EventEmitter {
  public element: HTMLElement;
  private playerData: PlayerData;

  constructor(playerData?: Partial<PlayerData>) {
    super();
    this.playerData = { ...DEFAULT_PLAYER, ...playerData };
    this.element = this.createElement();
  }

  /** Avatar inner HTML: an <img> when the value is a CDN/URL, else emoji/text. */
  private avatarInner(): string {
    const v = this.playerData.avatar ?? '';
    if (/^https?:\/\//i.test(v) || v.startsWith('/')) {
      const safe = v.replace(/"/g, '&quot;');
      return `<img class="home-avatar-img" src="${safe}" alt="">`;
    }
    const div = document.createElement('div');
    div.textContent = v;
    return div.innerHTML;
  }

  private formatNumber(num: number): string {
    if (num >= 1e12) return (num / 1e12).toFixed(1) + 'T';
    if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
    return num.toString();
  }

  private createElement(): HTMLElement {
    const screen = document.createElement('div');
    screen.innerHTML = `
      <div class="sdl-screen-content">
        <!-- Profile Section -->
        <section class="home-profile">
          <div class="home-avatar-container">
            <div class="home-avatar-ring"></div>
            <div class="home-avatar" data-action="profile">${this.avatarInner()}</div>
            <!-- League Division badge — reserved spot, not rendered until that system ships -->
          </div>
          <div class="home-username">${this.playerData.username}</div>
        </section>

        <!-- Stats Row -->
        <section class="home-stats-row">
          <button class="home-stat-btn home-stat-stacks" data-action="stacks">
            <img class="home-stat-icon" src="/assets/icons/currency/stacks.svg" alt="Stacks" />
            <span class="home-stat-value">${this.formatNumber(this.playerData.stacks)}</span>
          </button>
          <button class="home-stat-btn home-stat-gold" data-action="gold">
            <img class="home-stat-icon" src="/assets/icons/currency/gold.svg" alt="Gold" />
            <span class="home-stat-value">${this.formatNumber(this.playerData.gold)}</span>
          </button>
          <button class="home-stat-btn home-stat-notif${this.playerData.notifications > 0 ? ' has-notif' : ''}" data-action="notifications">
            <img class="home-stat-icon home-stat-icon-bell" src="/assets/icons/ui/bell.svg" alt="Notifications" />
            <span class="home-stat-value">${this.playerData.notifications}</span>
          </button>
          <button class="home-stat-btn home-stat-bonus${this.playerData.hasDailyBonus ? '' : ' claimed'}" data-action="daily-bonus">
            <img class="home-stat-icon home-stat-icon-gift" src="/assets/icons/ui/gift.svg" alt="Daily Bonus" />
            ${this.renderStreakBadge()}
          </button>
        </section>

        <!-- Progression / Events Section (placeholder) -->
        <section class="home-progression-slot">
          <!-- Reserved for progression modal, tournaments, events -->
        </section>

        <!-- Main Play Button -->
        <button class="home-main-play-btn" data-action="play" data-sound="button">
          <div class="home-play-btn-shine"></div>
          <div class="home-play-btn-content">
            <img class="home-play-btn-icon" src="/assets/icons/ui/dice-play.svg" alt="Play" />
            <div class="home-play-btn-text">
              <span class="home-play-btn-title">HIT THE BLOCK</span>
              <span class="home-play-btn-subtitle">Find a game now</span>
            </div>
          </div>
        </button>

        <!-- Game Modes Grid -->
        <section class="home-game-modes">
          <button class="home-mode-card home-mode-solo" data-action="solo" data-sound="button">
            <img class="home-mode-icon" src="/assets/icons/modes/solo.svg" alt="Solo" />
            <span class="home-mode-title">SOLO GRIND</span>
            <span class="home-mode-subtitle">Stack your bread</span>
          </button>

          <button class="home-mode-card home-mode-friends home-mode-card--locked" data-action="friends" data-sound="button">
            <img class="home-mode-icon" src="/assets/icons/modes/friends.svg" alt="Friends" />
            <span class="home-mode-title">RUN IT UP</span>
            <span class="home-mode-subtitle">Invite your crew</span>
            <div class="home-mode-locked">
              <img src="/assets/icons/ui/lock.svg" alt="Locked" />
            </div>
          </button>

          <button class="home-mode-card home-mode-leaderboard home-mode-card--locked" data-action="leaderboard" data-sound="button">
            <img class="home-mode-icon" src="/assets/icons/modes/leaderboard.svg" alt="Leaderboard" />
            <span class="home-mode-title">LEADERBOARD</span>
            <span class="home-mode-subtitle">Who's on top</span>
            <div class="home-mode-locked">
              <img src="/assets/icons/ui/lock.svg" alt="Locked" />
            </div>
          </button>

          <button class="home-mode-card home-mode-crew home-mode-card--locked" data-action="crew-wars" data-sound="button">
            <img class="home-mode-icon" src="/assets/icons/modes/crew-wars.svg" alt="Crew Wars" />
            <span class="home-mode-title">CREW WARS</span>
            <span class="home-mode-subtitle">Rep your set</span>
            <div class="home-mode-locked">
              <img src="/assets/icons/ui/lock.svg" alt="Locked" />
            </div>
          </button>
        </section>

        <!-- Version -->
        <div class="home-version">v0.1.0</div>
      </div>
    `;

    // Add styles
    this.injectStyles();

    // Setup event listeners
    this.setupEventListeners(screen);

    return screen;
  }

  private setupEventListeners(screen: HTMLElement): void {
    const buttons = screen.querySelectorAll('[data-action]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const action = (btn as HTMLElement).dataset.action;
        this.emit('action', { action });
      });
    });
  }

  private injectStyles(): void {
    if (document.getElementById('home-screen-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'home-screen-styles';
    styles.textContent = `
      /* Profile Section */
      .home-profile {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        padding-top: 10px;
      }

      .home-avatar-container {
        position: relative;
        width: 80px;
        height: 80px;
      }

      .home-avatar-ring {
        position: absolute;
        inset: -5px;
        border-radius: 50%;
        background: conic-gradient(
          from 0deg,
          var(--gold) 0deg,
          var(--gold-dim) 60deg,
          var(--gold-dark) 120deg,
          var(--gold-dim) 180deg,
          var(--gold) 240deg,
          var(--gold-dim) 300deg,
          var(--gold) 360deg
        );
        animation: sdl-ring-rotate 8s linear infinite;
      }

      .home-avatar-ring::before {
        content: '';
        position: absolute;
        inset: 4px;
        background: var(--dark-bg);
        border-radius: 50%;
      }

      .home-avatar {
        position: relative;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: linear-gradient(135deg, var(--concrete-lighter), var(--concrete));
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 36px;
        cursor: pointer;
        transition: transform 0.2s;
      }

      .home-avatar:active { transform: scale(0.95); }

      .home-avatar-img {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
      }

      .home-division-badge {
        /* Reserved for League Division — element is not rendered yet (see profile markup). */
        position: absolute;
        bottom: -2px;
        left: -2px;
        width: 28px;
        height: 28px;
        background: linear-gradient(135deg, var(--cyan), var(--cyan-dim));
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 700;
        color: var(--dark-bg);
        border: 2px solid var(--dark-bg);
        box-shadow: 0 2px 8px rgba(0, 245, 255, 0.4);
      }

      .home-username {
        font-family: var(--font-display);
        font-size: 28px;
        letter-spacing: 2px;
        color: white;
      }

      /* Stats Row */
      .home-stats-row {
        display: flex;
        justify-content: center;
        gap: 8px;
      }

      .home-stat-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 10px 14px;
        background: var(--glass);
        border: 1px solid var(--glass-border);
        border-radius: 25px;
        cursor: pointer;
        transition: all 0.2s;
        -webkit-tap-highlight-color: transparent;
      }

      .home-stat-btn:active {
        transform: scale(0.95);
        background: rgba(255, 255, 255, 0.06);
      }

      .home-stat-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      .home-stat-icon-bell,
      .home-stat-icon-gift {
        filter: brightness(0) invert(1);
      }

      /* Neon yellow glow when has notifications - static glow, no animation */
      .home-stat-notif.has-notif .home-stat-icon-bell {
        filter: brightness(0) saturate(100%) invert(89%) sepia(60%) saturate(1000%) hue-rotate(360deg) brightness(1.1) drop-shadow(0 0 6px #FFD700);
      }
      .home-stat-value {
        font-size: 15px;
        font-weight: 700;
        letter-spacing: 0.5px;
        font-family: var(--font-body);
      }

      .home-stat-stacks .home-stat-value { color: var(--green); }
      .home-stat-gold .home-stat-value { color: var(--gold); }
      .home-stat-notif .home-stat-value { color: white; }

      .home-stat-notif.has-notif {
        border-color: var(--gold);
        box-shadow: 0 0 12px rgba(255, 215, 0, 0.4);
      }

      .home-stat-notif.has-notif .home-stat-value {
        color: var(--gold);
      }

      .home-stat-bonus {
        border-color: var(--magenta-dim);
        background: rgba(255, 0, 128, 0.1);
        animation: sdl-bonus-shake 0.5s ease-in-out infinite;
      }

      .home-stat-bonus.claimed {
        animation: none;
        border-color: var(--glass-border);
        background: var(--glass);
        opacity: 0.5;
      }

      .bonus-cooldown-tooltip {
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
        padding: 6px 12px;
        background: rgba(0, 0, 0, 0.85);
        border: 1px solid var(--glass-border);
        border-radius: 8px;
        font-family: var(--font-body);
        font-size: 12px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.8);
        letter-spacing: 0.5px;
        pointer-events: none;
        opacity: 0;
        animation: sdl-cooldown-tooltip 2s ease-out forwards;
        z-index: 10;
      }

      @keyframes sdl-cooldown-tooltip {
        0% { opacity: 0; transform: translateX(-50%) translateY(4px); }
        8% { opacity: 1; transform: translateX(-50%) translateY(0); }
        85% { opacity: 1; transform: translateX(-50%) translateY(0); }
        100% { opacity: 0; transform: translateX(-50%) translateY(-4px); }
      }

      .home-stat-bonus {
        position: relative;
      }

      .bonus-streak-badge {
        position: absolute;
        top: -6px;
        right: -6px;
        min-width: 22px;
        height: 18px;
        padding: 0 4px;
        border-radius: 9px;
        background: var(--magenta);
        color: white;
        font-family: var(--font-display);
        font-size: 11px;
        letter-spacing: 0.5px;
        line-height: 18px;
        text-align: center;
        box-shadow: 0 0 8px rgba(255, 0, 128, 0.5);
        pointer-events: none;
      }

      /* Progression / Events Slot */
      .home-progression-slot {
        min-height: 20px;
        /* Reserved for progression modal, tournaments, events */
      }

      /* Main Play Button */
      .home-main-play-btn {
        position: relative;
        width: 100%;
        padding: 24px;
        background: linear-gradient(135deg, var(--gold), var(--gold-dim));
        border: none;
        border-radius: 16px;
        cursor: pointer;
        overflow: hidden;
        transition: all 0.3s;
        -webkit-tap-highlight-color: transparent;
      }

      .home-play-btn-shine {
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transform: translateX(-100%);
        animation: sdl-btn-shine 3s ease-in-out infinite;
      }

      .home-main-play-btn:active { transform: scale(0.98); }
      .home-main-play-btn:hover { box-shadow: 0 8px 40px rgba(255, 215, 0, 0.4); }

      .home-play-btn-content {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 15px;
      }

      .home-play-btn-icon {
        width: 36px;
        height: 36px;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
      }

      .home-play-btn-text {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }

      .home-play-btn-title {
        font-family: var(--font-display);
        font-size: 32px;
        letter-spacing: 3px;
        color: var(--dark-bg);
        line-height: 1;
      }

      .home-play-btn-subtitle {
        font-size: 13px;
        font-weight: 600;
        color: rgba(0, 0, 0, 0.6);
        letter-spacing: 1px;
      }

      /* Game Modes Grid */
      .home-game-modes {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
      }

      .home-mode-card {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 20px 15px;
        background: var(--concrete-light);
        border: 1px solid var(--glass-border);
        border-radius: 14px;
        cursor: pointer;
        overflow: hidden;
        transition: all 0.2s;
        -webkit-tap-highlight-color: transparent;
      }

      .home-mode-card::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: var(--gold);
        opacity: 0;
        transition: opacity 0.2s;
      }

      .home-mode-card:active {
        transform: scale(0.97);
        background: var(--concrete-lighter);
      }

      .home-mode-card:hover::after { opacity: 1; }

      .home-mode-solo::after { background: var(--cyan); }
      .home-mode-friends::after { background: var(--magenta); }
      .home-mode-leaderboard::after { background: var(--gold); }

      .home-mode-icon {
        width: 36px;
        height: 36px;
        filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
      }

      .home-mode-title {
        font-family: var(--font-display);
        font-size: 20px;
        letter-spacing: 2px;
        color: white;
      }

      .home-mode-subtitle {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.4);
        letter-spacing: 1px;
        text-transform: uppercase;
      }

      .home-mode-solo .home-mode-title { color: var(--cyan); }
      .home-mode-friends .home-mode-title { color: var(--magenta); }
      .home-mode-leaderboard .home-mode-title { color: var(--gold); }

      .home-mode-card--locked:hover::after { opacity: 0; }

      .home-mode-locked {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        backdrop-filter: blur(2px);
        border-radius: 14px;
      }

      .home-mode-locked img {
        width: 32px;
        height: 32px;
        filter: brightness(0) invert(1) opacity(0.8);
      }

      /* Version */
      .home-version {
        text-align: center;
        font-size: 10px;
        color: rgba(255, 255, 255, 0.15);
        padding: 10px 0 20px;
      }
    `;
    document.head.appendChild(styles);
  }

  private renderStreakBadge(): string {
    if (!this.playerData.hasDailyBonus) return '';
    const mult = this.playerData.bonusMultiplier;
    if (mult <= 1) return '';
    return `<span class="bonus-streak-badge">x${mult}</span>`;
  }

  /**
   * Update player data
   */
  updatePlayerData(data: Partial<PlayerData>): void {
    this.playerData = { ...this.playerData, ...data };
    const avatarEl = this.element.querySelector('.home-avatar');
    if (avatarEl) avatarEl.innerHTML = this.avatarInner();

    const divisionEl = this.element.querySelector('.home-division-badge');
    if (divisionEl) divisionEl.textContent = this.playerData.division;

    const usernameEl = this.element.querySelector('.home-username');
    if (usernameEl) usernameEl.textContent = this.playerData.username;

    const stacksValue = this.element.querySelector('.home-stat-stacks .home-stat-value');
    const goldValue = this.element.querySelector('.home-stat-gold .home-stat-value');
    const notifValue = this.element.querySelector('.home-stat-notif .home-stat-value');
    const notifBtn = this.element.querySelector('.home-stat-notif');
    const bonusBtn = this.element.querySelector('.home-stat-bonus');

    if (stacksValue) stacksValue.textContent = this.formatNumber(this.playerData.stacks);
    if (goldValue) goldValue.textContent = this.formatNumber(this.playerData.gold);
    if (notifValue) notifValue.textContent = this.playerData.notifications.toString();
    if (notifBtn) notifBtn.classList.toggle('has-notif', this.playerData.notifications > 0);
    if (bonusBtn) {
      bonusBtn.classList.toggle('claimed', !this.playerData.hasDailyBonus);
      // Update streak badge
      const existingBadge = bonusBtn.querySelector('.bonus-streak-badge');
      if (existingBadge) existingBadge.remove();
      const badgeHtml = this.renderStreakBadge();
      if (badgeHtml) bonusBtn.insertAdjacentHTML('beforeend', badgeHtml);
    }
  }

  updateFromServer(player: ServerPlayerData): void {
    const avatar = player.picture?.trim();
    this.updatePlayerData({
      username: player.username,
      level: player.level,
      stacks: player.balance.stacks,
      gold: player.balance.gold,
      // Always reflect the server's picture: an empty one (no custom avatar,
      // or a moderator removal) must fall back to the default, not keep the
      // stale image.
      avatar: avatar || DEFAULT_AVATAR,
    });
  }

  /**
   * Animate the stacks balance from current value to new value.
   * Used after daily bonus claim to show the increase.
   */
  /** Screen-space rect of the Stacks pill — flyout target for cash awards. */
  getStacksPillRect(): DOMRect | null {
    const el = this.element.querySelector('.home-stat-stacks');
    return el ? (el as HTMLElement).getBoundingClientRect() : null;
  }

  /** Screen-space rect of the daily-bonus gift button — coach-mark target. */
  getDailyBonusButtonRect(): DOMRect | null {
    const el = this.element.querySelector('.home-stat-bonus');
    return el ? (el as HTMLElement).getBoundingClientRect() : null;
  }

  /** Whether an unclaimed daily bonus is currently available. */
  hasDailyBonusAvailable(): boolean {
    return this.playerData.hasDailyBonus;
  }

  animateStacksIncrease(newBalance: number, duration = 1500, fromBalance?: number): void {
    const stacksValue = this.element.querySelector('.home-stat-stacks .home-stat-value');
    if (!stacksValue) return;

    const startBalance = fromBalance ?? this.playerData.stacks;
    if (newBalance <= startBalance) {
      this.updatePlayerData({ stacks: newBalance });
      return;
    }

    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const current = Math.round(startBalance + (newBalance - startBalance) * eased);
      stacksValue.textContent = this.formatNumber(current);

      if (t < 1) {
        requestAnimationFrame(tick);
      } else {
        // Set final value in player data
        this.playerData.stacks = newBalance;
      }
    };

    requestAnimationFrame(tick);
  }

  /**
   * Show a brief tooltip on the bonus button with time remaining until next bonus.
   */
  private cooldownTimer: ReturnType<typeof setInterval> | null = null;

  showCooldownTooltip(): void {
    const bonusBtn = this.element.querySelector('.home-stat-bonus');
    if (!bonusBtn) return;

    // Clear any existing tooltip + timer
    if (this.cooldownTimer) { clearInterval(this.cooldownTimer); this.cooldownTimer = null; }
    const existing = bonusBtn.querySelector('.bonus-cooldown-tooltip');
    if (existing) existing.remove();

    const nextAt = this.playerData.nextBonusAt;

    const formatRemaining = (): string => {
      if (!nextAt) return 'Come back later!';
      const remaining = nextAt - Date.now();
      if (remaining <= 0) return 'Ready!';
      const h = Math.floor(remaining / 3600000);
      const m = Math.floor((remaining % 3600000) / 60000);
      const s = Math.floor((remaining % 60000) / 1000);
      return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
    };

    const tooltip = document.createElement('span');
    tooltip.className = 'bonus-cooldown-tooltip';
    tooltip.textContent = formatRemaining();
    bonusBtn.appendChild(tooltip);

    // Tick every second while visible
    const DISPLAY_DURATION = 5000;
    this.cooldownTimer = setInterval(() => {
      tooltip.textContent = formatRemaining();
    }, 1000);

    // Remove after animation
    tooltip.style.animationDuration = `${DISPLAY_DURATION}ms`;
    setTimeout(() => {
      if (this.cooldownTimer) { clearInterval(this.cooldownTimer); this.cooldownTimer = null; }
      tooltip.remove();
    }, DISPLAY_DURATION + 100);
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }
}
